import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';

/**
 * Fires the card request lifecycle events exactly once per mount:
 * - `triggered.request` on mount
 * - `succeeded.request` or `failed.request` when loading resolves
 * - `rendered.content` when profile data is present and loading is done
 *
 * The phase ref ensures a strict 1:1 pairing between `triggered` and the
 * terminal event regardless of how many times `isLoading` toggles.
 */
export const useProfileCardRequestTracking = (isLoading: boolean, hasProfileData: boolean): void => {
	const { fireEvent } = useAnalyticsEvents();
	const phase = useRef<'idle' | 'triggered' | 'resolved'>('idle');

	useEffect(() => {
		if (phase.current === 'idle') {
			phase.current = 'triggered';
			fireEvent('operational.userProfileCard.triggered.request', {});
		}
		if (!isLoading && phase.current === 'triggered') {
			phase.current = 'resolved';
			fireEvent(
				hasProfileData
					? 'operational.userProfileCard.succeeded.request'
					: 'operational.userProfileCard.failed.request',
				{},
			);
			if (hasProfileData) {
				fireEvent('ui.userProfileCard.rendered.content', {});
			}
		}
	}, [isLoading, hasProfileData, fireEvent]);
};
